import React, { useState, useEffect } from 'react';
import { useEntitlements } from '../store/EntitlementStore';
import api from '../services/api';
import { Mail, MessageSquare, Send, Plus, MessageSquarePlus, Reply, ArrowLeft, ChevronRight } from 'lucide-react';
import './MessagesPage.css';

/**
 * Messages page - Supports both email-style messages and chat.
 */
export const MessagesPage = () => {
  const { user } = useEntitlements();
  const [activeTab, setActiveTab] = useState('messages'); // 'messages' or 'chat'
  const [messages, setMessages] = useState([]);
  const [conversations, setConversations] = useState([]);
  const [selectedConversation, setSelectedConversation] = useState(null);
  const [chatMessages, setChatMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [newMessageContent, setNewMessageContent] = useState('');
  const [selectedMessage, setSelectedMessage] = useState(null);
  const [messageReplies, setMessageReplies] = useState([]);
  const [showReplyForm, setShowReplyForm] = useState(false);
  const [replyData, setReplyData] = useState({
    body: '',
    subject: '',
  });
  
  // Compose message state
  const [showComposeMessage, setShowComposeMessage] = useState(false);
  const [composeMessageData, setComposeMessageData] = useState({
    recipients: [],
    client_recipients: [],
    subject: '',
    body: '',
  });
  
  // Start chat state
  const [showStartChat, setShowStartChat] = useState(false);
  const [startChatData, setStartChatData] = useState({
    conversation_type: 'client_team',
    client: '',
    participants: [],
    title: '',
  });
  
  // Available recipients/participants
  const [staffUsers, setStaffUsers] = useState([]);
  const [clients, setClients] = useState([]);
  
  const isStaff = user?.is_staff_user || user?.role === 'staff' || user?.role === 'admin';

  useEffect(() => {
    if (activeTab === 'messages') {
      fetchMessages();
    } else {
      fetchConversations();
    }
  }, [activeTab]);
  
  useEffect(() => {
    // Fetch staff users and clients for compose modals
    if (showComposeMessage || showStartChat) {
      fetchStaffUsers();
      fetchClients();
    }
  }, [showComposeMessage, showStartChat]);

  useEffect(() => {
    if (selectedConversation) {
      fetchChatMessages(selectedConversation.id);
      const interval = setInterval(() => {
        fetchChatMessages(selectedConversation.id);
      }, 3000); // Poll every 3 seconds
      return () => clearInterval(interval);
    }
  }, [selectedConversation]);

  const fetchMessages = async () => {
    try {
      setLoading(true);
      const response = await api.get('/v1/messages/');
      const allMessages = response.data.results || response.data;

      // Group messages by thread (only show top-level messages, not replies)
      // Replies will be shown when a message is selected
      const topLevelMessages = allMessages.filter(msg => !msg.parent_message);

      // Group by sender for better organization
      const groupedBySender = topLevelMessages.reduce((acc, msg) => {
        const senderKey = msg.sender_email || msg.sender;
        if (!acc[senderKey]) {
          acc[senderKey] = {
            senderName: msg.sender_name || msg.sender_email,
            senderEmail: msg.sender_email,
            messages: []
          };
        }
        acc[senderKey].messages.push(msg);
        return acc;
      }, {});

      setMessages(topLevelMessages);
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to fetch messages');
    } finally {
      setLoading(false);
    }
  };

  const fetchConversations = async () => {
    try {
      setLoading(true);
      const response = await api.get('/v1/chat/conversations/');
      setConversations(response.data.results || response.data);
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to fetch conversations');
    } finally {
      setLoading(false);
    }
  };

  const fetchChatMessages = async (conversationId) => {
    try {
      const response = await api.get(`/v1/chat/messages/?conversation=${conversationId}`);
      setChatMessages(response.data.results || response.data);
    } catch (err) {
      console.error('Failed to fetch chat messages:', err);
    }
  };

  const handleSendChatMessage = async () => {
    if (!newMessageContent.trim() || !selectedConversation) return;

    try {
      await api.post('/v1/chat/messages/', {
        conversation: selectedConversation.id,
        content: newMessageContent,
      });
      setNewMessageContent('');
      fetchChatMessages(selectedConversation.id);
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to send message');
    }
  };

  const handleMarkRead = async (message) => {
    try {
      await api.post(`/v1/messages/${message.id}/mark_read/`);
      fetchMessages();
    } catch (err) {
      console.error('Failed to mark message as read:', err);
    }
  };

  const fetchMessageReplies = async (messageId) => {
    try {
      const response = await api.get(`/v1/messages/?parent_message=${messageId}`);
      setMessageReplies(response.data.results || response.data);
    } catch (err) {
      console.error('Failed to fetch message replies:', err);
      setMessageReplies([]);
    }
  };

  const handleSelectMessage = async (message) => {
    setSelectedMessage(message);
    // Fetch replies for this message
    await fetchMessageReplies(message.id);
    // Mark as read when viewing
    if (message.status !== 'read') {
      await handleMarkRead(message);
    }
  };

  const handleReply = async (e) => {
    e.preventDefault();
    if (!selectedMessage || !replyData.body.trim()) {
      setError('Please enter a reply message');
      return;
    }

    try {
      const replyPayload = {
        body: replyData.body,
      };
      
      // Use custom subject if provided, otherwise backend will default to "Re: [original subject]"
      if (replyData.subject) {
        replyPayload.subject = replyData.subject;
      }

      await api.post(`/v1/messages/${selectedMessage.id}/reply/`, replyPayload);
      setShowReplyForm(false);
      setReplyData({ body: '', subject: '' });
      setError(null);
      // Refresh the replies for the current message
      await fetchMessageReplies(selectedMessage.id);
      // Also refresh the main messages list to update reply count
      fetchMessages();
    } catch (err) {
      let errorMsg = 'Failed to send reply';
      if (err.response?.data) {
        if (err.response.data.detail) {
          errorMsg = err.response.data.detail;
        } else if (err.response.data.error) {
          errorMsg = err.response.data.error;
        }
      }
      setError(errorMsg);
    }
  };
  
  const fetchStaffUsers = async () => {
    try {
      const response = await api.get('/v1/users/?role=staff');
      setStaffUsers(response.data.results || response.data);
    } catch (err) {
      console.error('Failed to fetch staff users:', err);
    }
  };
  
  const fetchClients = async () => {
    try {
      const response = await api.get('/v1/clients/');
      setClients(response.data.results || response.data);
    } catch (err) {
      console.error('Failed to fetch clients:', err);
    }
  };
  
  const handleSendMessage = async (e) => {
    e.preventDefault();
    try {
      // Ensure at least one recipient
      if (!composeMessageData.recipients.length && !composeMessageData.client_recipients.length) {
        setError('Please select at least one recipient');
        return;
      }
      
      // Convert recipient arrays to IDs for API (keep as strings for UUIDs)
      const messageData = {
        tenant: user?.tenant?.id,
        recipient_ids: composeMessageData.recipients.length > 0 
          ? composeMessageData.recipients.map(id => String(id))
          : [],
        client_recipient_ids: composeMessageData.client_recipients.length > 0
          ? composeMessageData.client_recipients.map(id => String(id))
          : [],
        subject: composeMessageData.subject,
        body: composeMessageData.body,
        status: 'sent',
      };
      
      await api.post('/v1/messages/', messageData);
      setShowComposeMessage(false);
      setComposeMessageData({
        recipients: [],
        client_recipients: [],
        subject: '',
        body: '',
      });
      setError(null);
      fetchMessages();
    } catch (err) {
      let errorMsg = 'Failed to send message';
      if (err.response?.data) {
        if (err.response.data.detail) {
          errorMsg = err.response.data.detail;
        } else if (err.response.data.error) {
          errorMsg = err.response.data.error;
        } else if (err.response.data.recipient_ids && Array.isArray(err.response.data.recipient_ids)) {
          errorMsg = err.response.data.recipient_ids[0];
        } else if (err.response.data.client_recipient_ids && Array.isArray(err.response.data.client_recipient_ids)) {
          errorMsg = err.response.data.client_recipient_ids[0];
        }
      }
      setError(errorMsg);
    }
  };
  
  const handleStartChat = async (e) => {
    e.preventDefault();
    try {
      // Convert participant array to IDs for API (keep as strings for UUIDs)
      const participantIds = startChatData.participants.map(id => String(id));
      if (user?.id && !participantIds.includes(String(user.id))) {
        participantIds.push(String(user.id));
      }
      
      const chatData = {
        tenant: user?.tenant?.id,
        conversation_type: startChatData.conversation_type,
        participant_ids: participantIds,
        title: startChatData.title || '',
      };
      
      if (startChatData.conversation_type === 'client_team' && startChatData.client) {
        chatData.client = String(startChatData.client);
      }
      
      const response = await api.post('/v1/chat/conversations/', chatData);
      setShowStartChat(false);
      setStartChatData({
        conversation_type: 'client_team',
        client: '',
        participants: [],
        title: '',
      });
      fetchConversations();
      setSelectedConversation(response.data);
    } catch (err) {
      setError(err.response?.data?.detail || err.response?.data?.error || 'Failed to start chat');
    }
  };

  return (
    <div className="messages-page">
      <div className="messages-header">
        <div className="messages-title">
          <Mail size={24} />
          <h1>Messages</h1>
        </div>
        <div className="messages-header-actions">
          {activeTab === 'messages' ? (
            <button
              className="btn-primary"
              onClick={() => setShowComposeMessage(true)}
              disabled={!isStaff && !user}
              title={!isStaff && !user ? 'Only staff can send messages' : 'Send Message'}
            >
              <Mail size={16} />
              Send Message
            </button>
          ) : (
            <button
              className="btn-primary"
              onClick={() => setShowStartChat(true)}
              title="Start Chat"
            >
              <MessageSquarePlus size={16} />
              Start Chat
            </button>
          )}
        </div>
        <div className="messages-tabs">
          <button
            className={`tab-button ${activeTab === 'messages' ? 'active' : ''}`}
            onClick={() => setActiveTab('messages')}
          >
            <Mail size={16} />
            In-App Messages
          </button>
          <button
            className={`tab-button ${activeTab === 'chat' ? 'active' : ''}`}
            onClick={() => setActiveTab('chat')}
          >
            <MessageSquare size={16} />
            Chat
          </button>
        </div>
      </div>

      {error && (
        <div className="error-banner">
          {error}
          <button onClick={() => setError(null)}>×</button>
        </div>
      )}

      {activeTab === 'messages' ? (
        <div className="messages-content">
          {loading ? (
            <div className="loading">Loading messages...</div>
          ) : selectedMessage ? (
            <div className="message-detail-view">
              <div className="message-detail-header">
                <button
                  className="btn-back"
                  onClick={() => {
                    setSelectedMessage(null);
                    setMessageReplies([]);
                    setShowReplyForm(false);
                    setReplyData({ body: '', subject: '' });
                  }}
                >
                  <ArrowLeft size={16} />
                  Back to Messages
                </button>
                <button
                  className="btn-primary"
                  onClick={() => setShowReplyForm(!showReplyForm)}
                >
                  <Reply size={16} />
                  Reply
                </button>
              </div>
              <div className="message-detail-content">
                <div className="message-detail-header-info">
                  <h2>{selectedMessage.subject}</h2>
                  <span className={`message-status-badge ${selectedMessage.status}`}>
                    {selectedMessage.status}
                  </span>
                </div>
                <div className="message-detail-meta">
                  <p><strong>From:</strong> {selectedMessage.sender_name || selectedMessage.sender_email}</p>
                  <p><strong>To:</strong> {
                    selectedMessage.recipient_emails?.length > 0
                      ? selectedMessage.recipient_emails.join(', ')
                      : selectedMessage.client_recipient_names?.join(', ') || 'N/A'
                  }</p>
                  <p><strong>Date:</strong> {new Date(selectedMessage.created_at).toLocaleString()}</p>
                  {selectedMessage.reply_count > 0 && (
                    <p><strong>Replies:</strong> {selectedMessage.reply_count}</p>
                  )}
                </div>
                <div className="message-detail-body">
                  <div dangerouslySetInnerHTML={{ __html: selectedMessage.body_html || selectedMessage.body.replace(/\n/g, '<br />') }} />
                </div>

                {messageReplies.length > 0 && (
                  <div className="message-replies">
                    <h3>Replies ({messageReplies.length})</h3>
                    {messageReplies.map((reply) => (
                      <div key={reply.id} className="reply-item">
                        <div className="reply-header">
                          <strong>{reply.sender_name || reply.sender_email}</strong>
                          <span className="reply-date">
                            {new Date(reply.created_at).toLocaleString()}
                          </span>
                        </div>
                        {reply.subject && reply.subject !== selectedMessage.subject && (
                          <div className="reply-subject">{reply.subject}</div>
                        )}
                        <div className="reply-body">
                          <div dangerouslySetInnerHTML={{ __html: reply.body_html || reply.body.replace(/\n/g, '<br />') }} />
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {showReplyForm && (
                <div className="reply-form-container">
                  <h3>Reply to Message</h3>
                  <form onSubmit={handleReply}>
                    <div className="form-group">
                      <label>Subject</label>
                      <input
                        type="text"
                        value={replyData.subject || `Re: ${selectedMessage.subject}`}
                        onChange={(e) => setReplyData({ ...replyData, subject: e.target.value })}
                        placeholder={`Re: ${selectedMessage.subject}`}
                      />
                    </div>
                    <div className="form-group">
                      <label>Your Reply</label>
                      <textarea
                        value={replyData.body}
                        onChange={(e) => setReplyData({ ...replyData, body: e.target.value })}
                        rows="8"
                        required
                        placeholder="Type your reply here..."
                      />
                    </div>
                    <div className="modal-actions">
                      <button
                        type="button"
                        onClick={() => {
                          setShowReplyForm(false);
                          setReplyData({ body: '', subject: '' });
                        }}
                      >
                        Cancel
                      </button>
                      <button type="submit" className="btn-primary">
                        <Reply size={16} />
                        Send Reply
                      </button>
                    </div>
                  </form>
                </div>
              )}
            </div>
          ) : messages.length === 0 ? (
            <div className="portalr-emptyState">
              <div className="portalr-emptyStateIcon" aria-hidden="true">
                <Mail size={20} />
              </div>
              <div className="portalr-emptyStateTitle">No messages yet</div>
              <p className="portalr-emptyStateDesc">
                Send your first update to keep your portal moving.
              </p>
              <div style={{ marginTop: '1rem' }}>
                <button
                  type="button"
                  className="portalr-btn portalr-btn--primary"
                  onClick={() => setShowComposeMessage(true)}
                >
                  Send a message
                  <ChevronRight size={18} aria-hidden="true" />
                </button>
              </div>
            </div>
          ) : (
            <div className="messages-list">
              {messages.map((message) => (
                <div
                  key={message.id}
                  className={`message-item ${message.status === 'read' ? 'read' : 'unread'}`}
                  onClick={() => handleSelectMessage(message)}
                >
                  <div className="message-header">
                    <h3>{message.subject}</h3>
                    <span className="message-status">{message.status}</span>
                  </div>
                  <p className="message-sender">From: {message.sender_name || message.sender_email}</p>
                  <p className="message-preview">{message.body.substring(0, 150)}...</p>
                  <p className="message-date">
                    {new Date(message.created_at).toLocaleString()}
                  </p>
                  {message.reply_count > 0 && (
                    <span className="reply-count">{message.reply_count} {message.reply_count === 1 ? 'reply' : 'replies'}</span>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      ) : (
        <div className="chat-content">
          <div className="chat-sidebar">
            <div className="chat-conversations">
              {conversations.map((conv) => (
                <div
                  key={conv.id}
                  className={`chat-conversation-item ${
                    selectedConversation?.id === conv.id ? 'active' : ''
                  }`}
                  onClick={() => setSelectedConversation(conv)}
                >
                  <div className="conversation-info">
                    <h4>{conv.title || (conv.client_name || 'Team Chat')}</h4>
                    <p className="conversation-preview">{conv.last_message_preview}</p>
                  </div>
                  {conv.unread_count > 0 && (
                    <span className="unread-badge">{conv.unread_count}</span>
                  )}
                </div>
              ))}
            </div>
          </div>
          <div className="chat-main">
            {selectedConversation ? (
              <>
                <div className="chat-messages">
                  {chatMessages.map((msg) => (
                    <div
                      key={msg.id}
                      className={`chat-message ${
                        msg.sender === user?.id ? 'sent' : 'received'
                      }`}
                    >
                      <div className="chat-message-content">
                        <p>{msg.content}</p>
                        <span className="chat-message-time">
                          {new Date(msg.created_at).toLocaleTimeString()}
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
                <div className="chat-input">
                  <input
                    type="text"
                    value={newMessageContent}
                    onChange={(e) => setNewMessageContent(e.target.value)}
                    onKeyPress={(e) => {
                      if (e.key === 'Enter') {
                        handleSendChatMessage();
                      }
                    }}
                    placeholder="Type a message..."
                  />
                  <button onClick={handleSendChatMessage} className="btn-primary">
                    <Send size={16} />
                  </button>
                </div>
              </>
            ) : (
              <div className="chat-empty">
                <div className="portalr-emptyState" style={{ padding: 0 }}>
                  <div className="portalr-emptyStateIcon" aria-hidden="true">
                    <MessageSquare size={20} />
                  </div>
                  <div className="portalr-emptyStateTitle">Ready when you are</div>
                  <p className="portalr-emptyStateDesc">
                    Start a new chat to coordinate with your team and clients.
                  </p>
                  <div style={{ marginTop: '1rem' }}>
                    <button
                      type="button"
                      className="portalr-btn portalr-btn--primary"
                      onClick={() => setShowStartChat(true)}
                    >
                      Start Chat
                      <ChevronRight size={18} aria-hidden="true" />
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      )}
      
      {/* Compose Message Modal */}
      {showComposeMessage && (
        <div className="modal-overlay" onClick={() => setShowComposeMessage(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <h2>Send Message</h2>
            <form onSubmit={handleSendMessage}>
              {isStaff ? (
                <>
                  <div className="form-group">
                    <label>Recipients (Staff)</label>
                    <select
                      multiple
                      value={composeMessageData.recipients}
                      onChange={(e) => {
                        const selected = Array.from(e.target.selectedOptions, option => option.value);
                        setComposeMessageData({ ...composeMessageData, recipients: selected });
                      }}
                    >
                      {staffUsers.map((staff) => (
                        <option key={staff.id} value={staff.id}>
                          {staff.first_name} {staff.last_name} ({staff.email})
                        </option>
                      ))}
                    </select>
                  </div>
                  <div className="form-group">
                    <label>Client Recipients</label>
                    <select
                      multiple
                      value={composeMessageData.client_recipients}
                      onChange={(e) => {
                        const selected = Array.from(e.target.selectedOptions, option => option.value);
                        setComposeMessageData({ ...composeMessageData, client_recipients: selected });
                      }}
                    >
                      {clients.map((client) => (
                        <option key={client.id} value={client.id}>
                          {client.name}
                        </option>
                      ))}
                    </select>
                  </div>
                </>
              ) : (
                <div className="form-group">
                  <label>Recipients (Staff Only)</label>
                  <select
                    multiple
                    value={composeMessageData.recipients}
                    onChange={(e) => {
                      const selected = Array.from(e.target.selectedOptions, option => option.value);
                      setComposeMessageData({ ...composeMessageData, recipients: selected });
                    }}
                    required
                  >
                    {staffUsers.map((staff) => (
                      <option key={staff.id} value={staff.id}>
                        {staff.first_name} {staff.last_name} ({staff.email})
                      </option>
                    ))}
                  </select>
                </div>
              )}
              <div className="form-group">
                <label>Subject</label>
                <input
                  type="text"
                  value={composeMessageData.subject}
                  onChange={(e) => setComposeMessageData({ ...composeMessageData, subject: e.target.value })}
                  required
                />
              </div>
              <div className="form-group">
                <label>Message</label>
                <textarea
                  value={composeMessageData.body}
                  onChange={(e) => setComposeMessageData({ ...composeMessageData, body: e.target.value })}
                  rows="8"
                  required
                />
              </div>
              <div className="modal-actions">
                <button type="button" onClick={() => setShowComposeMessage(false)}>
                  Cancel
                </button>
                <button type="submit" className="btn-primary">
                  <Send size={16} />
                  Send
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
      
      {/* Start Chat Modal */}
      {showStartChat && (
        <div className="modal-overlay" onClick={() => setShowStartChat(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <h2>Start Chat</h2>
            <form onSubmit={handleStartChat}>
              <div className="form-group">
                <label>Chat Type</label>
                <select
                  value={startChatData.conversation_type}
                  onChange={(e) => setStartChatData({ ...startChatData, conversation_type: e.target.value, client: '' })}
                  disabled={!isStaff}
                >
                  <option value="client_team">Client-Team Chat</option>
                  {isStaff && <option value="team_only">Team-Only Chat</option>}
                </select>
              </div>
              {startChatData.conversation_type === 'client_team' && (
                <div className="form-group">
                  <label>Client</label>
                  <select
                    value={startChatData.client}
                    onChange={(e) => setStartChatData({ ...startChatData, client: e.target.value })}
                    required
                  >
                    <option value="">Select a client</option>
                    {clients.map((client) => (
                      <option key={client.id} value={client.id}>
                        {client.name}
                      </option>
                    ))}
                  </select>
                </div>
              )}
              {startChatData.conversation_type === 'team_only' && (
                <div className="form-group">
                  <label>Participants (Staff)</label>
                  <select
                    multiple
                    value={startChatData.participants}
                    onChange={(e) => {
                      const selected = Array.from(e.target.selectedOptions, option => option.value);
                      setStartChatData({ ...startChatData, participants: selected });
                    }}
                    required
                  >
                    {staffUsers.map((staff) => (
                      <option key={staff.id} value={staff.id}>
                        {staff.first_name} {staff.last_name} ({staff.email})
                      </option>
                    ))}
                  </select>
                </div>
              )}
              <div className="form-group">
                <label>Title (Optional)</label>
                <input
                  type="text"
                  value={startChatData.title}
                  onChange={(e) => setStartChatData({ ...startChatData, title: e.target.value })}
                  placeholder="Chat title"
                />
              </div>
              <div className="modal-actions">
                <button type="button" onClick={() => setShowStartChat(false)}>
                  Cancel
                </button>
                <button type="submit" className="btn-primary">
                  <MessageSquarePlus size={16} />
                  Start Chat
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

